import { ISelectOption, ObjectionCategory, ITreeItemFilter } from "@/models/shared.model";
import { ITranscriptionQueryGroup, TranscriptionQueryConjunctions, TranscriptionQueryOperators } from "@/models/transcription.model";
import { IScorecardFilter } from "@/models/scorecardFilter.model";
import { Sentiment } from "@/models/sentiment.model";

export interface IFilter {
  campaignIds: number[] | undefined;
  teamIds: number[] | undefined;
  userIds: number[] | undefined;
  queueIds: number[] | undefined;
  resultCodes: string[] | undefined;
  recordingLength: IRecordingLength | undefined;
  callDirections: string[] | undefined;
  lists: string[] | undefined;
  daysOfWeek: DaysOfWeek[] | undefined;
  shortCalls: ShortCallsOptions;
  transcriptSearch: ITranscriptSearchFilter;
  isScored?: boolean;
  scorecardFilter?: IScorecardFilter | undefined;
  isSuccess?: boolean;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  postalCode?: string;
  referenceId?: string;
  topics?: string[] | undefined;
  overallSentiments?: string[] | undefined;
  peakSentiments?: string[] | undefined;
  endSentiments?: string[] | undefined;
  objections?: ITreeItemFilter[] | undefined;
  dataSupplierIds: number[] | undefined;
}

export interface IFilterState {
  id?: string;
  name?: string
  folderId?: string;
  filter: IFilter;
  chips: IFilterChips[];
  isDefault?: boolean;
}

export interface IRecordingLength {
  min: number;
  max: number;
}

export interface ITranscriptSearchFilter {
  conjunction: TranscriptionQueryConjunctions;
  options: ITranscriptSearchFilterOption[];
}

export interface ITranscriptSearchFilterOption {
  type: TranscriptSearchFilterOptionTypes;
  value: ITranscriptSearchFilterCondition | ITranscriptSearchFilterGroup;
}

export interface ITranscriptSearchFilterCondition {
  operator: TranscriptionQueryOperators;
  text: string | undefined;
}

export interface ITranscriptSearchFilterGroup {
  conjunction: TranscriptionQueryConjunctions;
  conditions: ITranscriptSearchFilterCondition[];
}

export interface IFilterOptions {
  campaignIds?: number[];
  teamIds?: number[];
  queueIds?: number[];
  userIds?: number[];
  resultCodes?: string[];
  durations?: string[];
  callDirections?: string[];
  lists?: string[];
  daysOfWeek?: string[];
  excludeShortCalls?: boolean;
  onlyShortCalls?: boolean;
  isSuccess?: boolean;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  postalCode?: string;
  referenceId?: string;
  topics?: string[];
  overallSentiments?: string[];
  peakSentiments?: string[];
  endSentiments?: string[];
  objectionNames?: string[];
  objectionCategories?: string[];
  dataSupplierIds?: number[];
  queryGroups?: ITranscriptionQueryGroup[];
  isScored?: boolean;
  scorecardFilter?: IScorecardFilter | undefined;
}

export interface IFilterChips {
  id?: string;
  property: keyof IFilter;
  label: string;
  tooltipText?: string;
  showTooltip?: boolean;
}

export interface IFilterChipsEventData {
  itemId: string | undefined,
  property: keyof IFilter,
}

export interface IContactInfoFilter {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  postalCode?: string;
  referenceId?: string;
}

export interface IFilterResponse {
  campaigns: IOptionResponse<number>[];
  teams: IOptionResponse<number>[];
  queues: IOptionResponse<number>[];
  users: IOptionResponse<number>[];
  resultCodes: IOptionResponse<string>[];
  callDurations: IOptionResponse<string>[];
  callDirections: IOptionResponse<string>[];
  lists: IOptionResponse<string>[];
  topics: IOptionResponse<string>[];
  objections: IOptionResponse<IObjectionFilter>[];
  dataSuppliers: IOptionResponse<number>[];
  scorecards: IOptionResponse<string>[];
}

export interface IOptionResponse<TValue> {
  value: TValue;
  name: string;
}

export interface IObjectionFilter {
  name: string;
  category: ObjectionCategory;
}

export interface IFilterViewModel {
  campaigns: ISelectOption<number>[];
  teams: ISelectOption<number>[];
  queues: ISelectOption<number>[];
  users: ISelectOption<number>[];
  resultCodes: ISelectOption<string>[];
  callDirections: ISelectOption<string>[];
  lists: ISelectOption<string>[];
  shortCalls: ISelectOption<ShortCallsOptions>[];
  daysOfWeek: ISelectOption<DaysOfWeek>[];
  minDuration: number;
  maxDuration: number;
  topics: ISelectOption<string>[];
  overallSentiments: ISelectOption<Sentiment>[];
  peakSentiments: ISelectOption<Sentiment>[];
  endSentiments: ISelectOption<Sentiment>[];
  objections: ISelectOption<ObjectionCategory>[];
  dataSuppliers: ISelectOption<number>[];
  scorecards: ISelectOption<string>[];
}

export enum FilterMode {
  regular = 0,
  viewDefault,
  viewSelected
}

export enum TranscriptSearchFilterOptionTypes {
  condition = "condition",
  group = "group",
}

export enum ShortCallsOptions {
  all = "All",
  showOnlyAiProcessed = "ShowAiProcessed",
  showOnlyAiNotProcessed = "ShowAiNotProcessed",
}

export enum Granularity {
  DAY = "Day",
  WEEK = "Week",
  MONTH = "Month"
}

export enum DaysOfWeek {
  MONDAY = "Monday",
  TUESDAY = "Tuesday",
  WEDNESDAY = "Wednesday",
  THURSDAY = "Thursday",
  FRIDAY = "Friday",
  SATURDAY = "Saturday",
  SUNDAY = "Sunday",
}